import type { WorkItemSummary } from '../types/workflow.js';
import type { EntityRef, GroupNode } from '../api/client.js';

export interface LessonContextNode {
  id: string;
  label: string;
  resolved: boolean;
}

export interface LessonContext {
  prerequisites: LessonContextNode[];
  lesson: {
    title: string;
    group: LessonContextNode | null;
  };
  dependents: LessonContextNode[];
}

export function deriveLessonContext(
  patch: Record<string, unknown>,
  groups: GroupNode[],
  dependents: EntityRef[],
  // Prerequisite keys live in the ENTITY namespace (they reference other lessons),
  // not the group namespace — resolve them against entity titles, never group titles.
  prerequisiteTitles: ReadonlyMap<string, string>,
  item?: Pick<WorkItemSummary, 'lessonPlacement'> | null,
): LessonContext {
  const groupLabels = flattenGroupLabels(groups);
  const groupId = stringId(patch.groupId ?? item?.lessonPlacement?.groupId);

  return {
    prerequisites: prerequisiteIds(patch.prerequisites).map((id) => ({
      id,
      label: prerequisiteTitles.get(id) ?? id,
      resolved: prerequisiteTitles.has(id),
    })),
    lesson: {
      title: nonEmptyString(patch.title) ?? 'Proposed lesson',
      group: groupId == null
        ? null
        : {
            id: groupId,
            label: groupLabels.get(groupId) ?? groupId,
            resolved: groupLabels.has(groupId),
          },
    },
    dependents: dependents.flatMap((dependent) => {
      const id = stringId(dependent.id);
      if (id == null) {
        return [];
      }
      const title = nonEmptyString(dependent.title);
      return [{ id, label: title ?? id, resolved: title != null }];
    }),
  };
}

/** Numeric prerequisite entity ids worth fetching titles for. */
export function prerequisiteEntityIds(patch: Record<string, unknown>): number[] {
  return prerequisiteIds(patch.prerequisites)
    .map((id) => Number(id))
    .filter((id) => Number.isInteger(id) && id >= 0);
}

function prerequisiteIds(value: unknown): string[] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return [];
  }
  return [...new Set(Object.keys(value as Record<string, unknown>).map((id) => id.trim()).filter(Boolean))];
}

function flattenGroupLabels(groups: GroupNode[]): Map<string, string> {
  const labels = new Map<string, string>();
  const remaining: unknown[] = [...groups];
  const visited = new Set<unknown>();

  while (remaining.length > 0) {
    const candidate = remaining.shift();
    if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate) || visited.has(candidate)) {
      continue;
    }
    visited.add(candidate);
    const group = candidate as Partial<GroupNode>;
    const id = stringId(group.id);
    const title = nonEmptyString(group.title);
    if (id != null && title != null) {
      labels.set(id, title);
    }
    if (Array.isArray(group.children)) {
      remaining.push(...group.children);
    }
  }

  return labels;
}

function stringId(value: unknown): string | null {
  if ((typeof value === 'number' && Number.isFinite(value)) || typeof value === 'string') {
    const id = String(value).trim();
    return id || null;
  }
  return null;
}

function nonEmptyString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}
